import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useGetClientRowQuery, useGetClientPoliciesQuery } from '@/features/clients/clientsApi';

// location.state.policies can mix several shapes: fully-resolved policy objects (eg. a bulk
// selection), bare `{ Id }` references, or a plain id (number/string) on its own. Anything that
// isn't already a resolved policy object is treated as an id and resolved against the client's
// policy list rather than one lookup per id
export const useLocationStatePolicies = (clientId) => {
	const location = useLocation();
	const policies = useMemo(() => location?.state?.policies ?? [], [location?.state?.policies]);

	const policyObjects = useMemo(
		() => policies.filter((p) => p && typeof p === 'object' && p.PolicyNumber != null),
		[policies],
	);

	const policyIds = useMemo(() => {
		return policies
			.map((p) => {
				if (p == null) return null;
				if (typeof p === 'number' || typeof p === 'string') return p;
				if (typeof p === 'object') {
					return p.PolicyId ?? p.Id ?? p.policyId ?? p.insightId ?? null;
				}
				return null;
			})
			.filter((id) => id != null)
			.map(String);
	}, [policies]);

	const { data: clientRow } = useGetClientRowQuery(clientId, {
		skip: !clientId || policyIds.length === 0,
	});

	const { data: clientPolicies } = useGetClientPoliciesQuery(
		{ insightId: clientRow?.insightId, clientId },
		{ skip: !clientRow?.insightId || policyIds.length === 0 },
	);

	const existingPolicies = useMemo(() => {
		if (policyIds.length === 0) return policyObjects;
		const hydrated = (clientPolicies ?? []).filter((p) => policyIds.includes(String(p.Id)));
		return [...policyObjects, ...hydrated];
	}, [policyObjects, policyIds, clientPolicies]);

	return { existingPolicy: existingPolicies[0] ?? {}, existingPolicies };
};
